/**
 * RemusSections — zeigt für Remus / Bastuck Produkte folgende Bereiche:
 *   • Dokumente / Gutachten als PDF-Links
 *   • Sound-Dateien als Inline-Audio-Player
 *   • Passend für folgende Fahrzeuge (Tabelle)
 *   • Dazugehörige Produkte (related_aliases) als Karten
 *
 * Erscheint nur, wenn mindestens eines der vier Felder vorhanden ist.
 * Wiederverwendbar für andere Hersteller, die dieselbe Datenstruktur liefern.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Car, Package } from 'lucide-react';
import { cdnImage } from '../../utils/cdn';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtPrice(p) {
  if (p == null) return '';
  return p.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function absUrl(u) {
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('/')) return API + u;
  return u;
}

// Mappt den DE-Report-Namen (vehicles[].tuev_de) auf eine kurze Zulassungs-Bezeichnung.
// Spiegelt die Backend-Logik (_approval_short_label) für korrekte Anzeige (ABE/Teilegutachten/TTG).
function approvalLabel(name) {
  const t = (name || '').trim().toLowerCase();
  if (!t) return '';
  if (t.includes('teilegutachten')) return 'Teilegutachten';
  if (t.includes('teiletypgenehmigung') || /\bttg\b/.test(t)) return 'Teiletypgenehmigung (TTG)';
  if (t.includes('allgemeine betriebserlaubnis') || /\babe\b/.test(t)) return 'ABE';
  if (t.includes('eg-genehmigung') || t.includes('eg genehmigung') || t.includes('ece') || t.includes('e-genehmigung')) return 'EG-/ECE-Genehmigung';
  if (t.includes('ohne straßenzulassung') || t.includes('ohne strassenzulassung') || t.includes('rennsport') || t.includes('nicht für den') || t.includes('keine straßenzulassung')) return 'ohne Straßenzulassung';
  return name.replace(/\s+/g, ' ').trim();
}

// Normalisiert Fahrzeug-Keys: APR/Dinan/HJS/Stoll liefern manufacturer/model/type/hp/motor_code/tuev_de,
// Remus/Bastuck liefern marke/modell/typ/baujahr. Beide Varianten unterstützen.
function normVehicle(v) {
  return {
    marke: v.marke || v.manufacturer || '',
    modell: v.modell || v.model || '',
    typ: v.typ || v.type || '',
    baujahr: v.baujahr || v.year || '',
    hp: v.hp || v.ps || '',
    motorCode: v.motor_code || v.motorcode || v.motorkennbuchstabe || '',
    zulassung: approvalLabel(v.tuev_de || v.tuev || ''),
    deLegal: v.de_legal,
  };
}

function VehicleSearch({ vehicles: rawVehicles }) {
  const [q, setQ] = useState('');
  const vehicles = useMemo(() => (rawVehicles || []).map(normVehicle), [rawVehicles]);
  const hasMotor = useMemo(() => vehicles.some(v => v.motorCode), [vehicles]);
  const hasZulassung = useMemo(() => vehicles.some(v => v.zulassung), [vehicles]);
  const filtered = useMemo(() => {
    if (!q.trim()) return vehicles;
    const t = q.toLowerCase();
    return vehicles.filter(v => {
      const all = `${v.marke} ${v.modell} ${v.typ} ${v.baujahr} ${v.motorCode} ${v.zulassung}`.toLowerCase();
      return all.includes(t);
    });
  }, [q, vehicles]);
  const colCount = 3 + (hasMotor ? 1 : 0) + (hasZulassung ? 1 : 0);
  const th = { padding: '10px 12px', borderBottom: '1px solid #222', fontWeight: 600 };
  return (
    <>
      <input
        type="text"
        placeholder="Fahrzeug suchen (Marke, Typ, Motorcode...)"
        value={q}
        onChange={e => setQ(e.target.value)}
        data-testid="remus-vehicle-search"
        style={{
          width: '100%', maxWidth: 400, padding: '8px 12px',
          background: '#0a0a0a', border: '1px solid #2a2a2a',
          color: '#e0e0e0', borderRadius: 4, marginBottom: 12,
          fontSize: 14,
        }}
      />
      <div style={{ overflowX: 'auto' }}>
        <table className="rs-vehicle-table" data-testid="remus-vehicles-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#0e0e0e', color: '#aaa', textAlign: 'left' }}>
              <th style={th}>Marke</th>
              <th style={th}>Modell</th>
              <th style={th}>Typ</th>
              {hasMotor && <th style={th}>Motorkennbuchstabe</th>}
              {hasZulassung && <th style={th}>Zulassung</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={colCount} style={{ padding: 16, color: '#666' }}>Keine Treffer</td></tr>
            ) : filtered.map((v, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #181818' }}>
                <td style={{ padding: '10px 12px', color: '#e0e0e0', fontWeight: 600 }}>{v.marke || '—'}</td>
                <td style={{ padding: '10px 12px', color: '#bbb' }}>{v.modell || '—'}</td>
                <td style={{ padding: '10px 12px', color: '#999' }}>
                  {v.typ || '—'}
                  {v.baujahr ? ` (${v.baujahr})` : ''}
                  {v.hp ? ` · ${v.hp} PS` : ''}
                </td>
                {hasMotor && (
                  <td style={{ padding: '10px 12px', color: '#e0e0e0', fontWeight: 600, whiteSpace: 'nowrap' }}
                      data-testid={`remus-vehicle-motorcode-${i}`}>
                    {v.motorCode || '—'}
                  </td>
                )}
                {hasZulassung && (
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}
                      data-testid={`remus-vehicle-zulassung-${i}`}>
                    {v.zulassung
                      ? <span style={{
                          color: v.deLegal === false ? '#ffb86b' : '#9aff9a',
                          fontWeight: 500,
                        }}>{v.zulassung}</span>
                      : <span style={{ color: '#666' }}>—</span>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {vehicles.length > 30 && q.trim() === '' && (
        <p style={{ color: '#888', fontSize: 13, marginTop: 8 }}>
          {vehicles.length} kompatible Fahrzeuge insgesamt — Suche oben benutzen
        </p>
      )}
    </>
  );
}

export default function RemusSections({ product }) {
  const sounds = Array.isArray(product?.sound_files) ? product.sound_files : [];
  const docs = Array.isArray(product?.documents) ? product.documents : [];
  const vehicles = Array.isArray(product?.vehicles) ? product.vehicles : [];
  // related_aliases is stored as [{sku, alias}, ...] in DB — extract alias strings.
  // Also accept plain string arrays for backwards-compatibility.
  const relatedAliases = Array.isArray(product?.related_aliases)
    ? product.related_aliases
        .map(r => (r && typeof r === 'object') ? r.alias : r)
        .filter(a => a && a !== product.alias)
    : [];

  const [related, setRelated] = useState([]);
  useEffect(() => {
    if (relatedAliases.length === 0) {
      setRelated([]);
      return;
    }
    let abort = false;
    fetch(`${API}/api/shop/products-by-aliases?aliases=${encodeURIComponent(relatedAliases.join(','))}`)
      .then(r => r.ok ? r.json() : { items: [] })
      .then(data => { if (!abort) setRelated(data.items || []); })
      .catch(() => { if (!abort) setRelated([]); });
    return () => { abort = true; };
  }, [relatedAliases.join(',')]);

  // Sounds werden im RemusHeroSounds (oben auf der Seite) gerendert
  const hasAny = docs.length || vehicles.length || relatedAliases.length;
  if (!hasAny) return null;

  const sectionStyle = {
    maxWidth: 1200, margin: '0 auto', padding: '32px 1.5rem 0',
    color: '#e0e0e0',
  };
  const headingStyle = {
    fontSize: '1.4rem', fontWeight: 700, color: '#fff',
    margin: '32px 0 16px', display: 'flex', alignItems: 'center', gap: 10,
    paddingBottom: 8, borderBottom: '1px solid #1f1f1f',
  };

  // Vehicle headline: take first vehicle as a quick "fits ..." badge
  const v0 = vehicles[0] ? normVehicle(vehicles[0]) : null;
  let v0Label = '';
  if (v0) {
    const parts = [v0.marke, v0.modell, v0.typ].filter(Boolean);
    v0Label = parts.join(' ') + (v0.baujahr ? ` (${v0.baujahr})` : '');
  }

  return (
    <section className="rs-remus" data-testid="remus-sections" style={sectionStyle}>
      {/* Dokumente / Gutachten */}
      {docs.length > 0 && (
        <>
          <h2 style={headingStyle} data-testid="remus-documents-heading">
            <FileText size={20} color="#ff2e2e" /> Dokumente / Gutachten ({docs.length})
          </h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} data-testid="remus-documents-list">
            {docs.map((d, i) => (
              <li key={i} style={{ marginBottom: 8 }}>
                <a
                  href={absUrl(d.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`remus-doc-link-${i}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    color: '#ff2e2e', textDecoration: 'none',
                    background: '#0a0a0a', border: '1px solid #2a2a2a',
                    padding: '10px 16px', borderRadius: 4, fontSize: 14,
                  }}
                >
                  <FileText size={16} />
                  <span>{d.name || 'Dokument'} (PDF)</span>
                </a>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Sound-Dateien werden nun als Hero-Element oben gerendert (RemusHeroSounds) */}

      {/* Passend für folgende Fahrzeuge */}
      {vehicles.length > 0 && (
        <>
          <h2 style={headingStyle} data-testid="remus-vehicles-heading">
            <Car size={20} color="#ff2e2e" /> Passend für folgende Fahrzeuge ({vehicles.length})
          </h2>
          {v0Label && (
            <p style={{ color: '#9aff9a', background: '#0d1f0d', border: '1px solid #1d3a1d',
                        padding: '8px 12px', borderRadius: 4, fontSize: 14, margin: '0 0 16px' }}>
              ✓ Passend für: <strong style={{ color: '#fff' }}>{v0Label}</strong>
            </p>
          )}
          <VehicleSearch vehicles={vehicles} />
        </>
      )}

      {/* Dazugehörige Produkte */}
      {related.length > 0 && (
        <>
          <h2 style={headingStyle} data-testid="remus-related-heading">
            <Package size={20} color="#ff2e2e" /> Dazugehörige Produkte ({related.length})
          </h2>
          <p style={{ color: '#888', fontSize: 14, margin: '0 0 16px' }}>
            Komponenten und Endrohr-Sets, die zur Komplettanlage gehören:
          </p>
          <div className="rs-related-grid" data-testid="remus-related-grid"
               style={{
                 display: 'grid',
                 gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                 gap: 16,
               }}>
            {related.map(rp => (
              <Link
                key={rp.alias}
                to={`/p/${rp.slug}`}
                data-testid={`remus-related-card-${rp.alias}`}
                style={{
                  display: 'flex', flexDirection: 'column',
                  background: '#0a0a0a', border: '1px solid #1f1f1f',
                  borderRadius: 4, overflow: 'hidden',
                  textDecoration: 'none', color: 'inherit',
                  transition: 'border-color 0.15s, transform 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#ff2e2e'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#1f1f1f'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{
                  background: '#fff', height: 160, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', padding: 12,
                }}>
                  <img
                    src={cdnImage(rp.image_url) || '/images/placeholder.svg'}
                    alt={rp.name}
                    loading="lazy"
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    onError={e => { e.target.src = '/images/placeholder.svg'; }}
                  />
                </div>
                <div style={{ padding: 12, display: 'flex', flexDirection: 'column', flex: 1 }}>
                  {(rp.han || rp.manufacturer_sku) && (
                    <span style={{ color: '#666', fontSize: 11, marginBottom: 4 }}>
                      SKU: {rp.han || rp.manufacturer_sku}
                    </span>
                  )}
                  <p style={{
                    color: '#e0e0e0', fontSize: 13, fontWeight: 500,
                    margin: '0 0 8px', display: '-webkit-box', WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    minHeight: 36, lineHeight: 1.3,
                  }}>
                    {rp.name}
                  </p>
                  {rp.price > 0 && (
                    <span style={{ color: '#fff', fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
                      {fmtPrice(rp.price)}
                    </span>
                  )}
                  <span style={{
                    background: '#ff2e2e', color: '#fff', textAlign: 'center',
                    padding: '8px 12px', fontSize: 13, fontWeight: 600,
                    marginTop: 'auto', borderRadius: 2,
                  }}>
                    Zum Produkt
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
